import React, { useState } from 'react';
import { 
  Folder, 
  FolderOpen, 
  ChevronRight, 
  ChevronDown, 
  File,
  FolderPlus,
  Edit2,
  Trash2,
  AlertTriangle,
  AlertCircle,
  X
} from 'lucide-react';

// ============================================================================
// COMPONENTI DIALOGO
// ============================================================================

/**
 * Componente InputDialog per inserimento testo (creazione/rinominazione)
 */
function InputDialog({ isOpen, onClose, onConfirm, title, message, placeholder, defaultValue = '', confirmText = "Conferma", cancelText = "Annulla", type = "info" }) {
  const [inputValue, setInputValue] = useState(defaultValue);

  React.useEffect(() => {
    if (isOpen) {
      setInputValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  if (!isOpen) return null;

  const typeConfig = {
    info: {
      icon: <FolderPlus className="w-12 h-12 text-blue-500" />,
      confirmBg: 'bg-blue-600 hover:bg-blue-700',
      iconBg: 'bg-blue-100'
    },
    warning: {
      icon: <Edit2 className="w-12 h-12 text-yellow-500" />,
      confirmBg: 'bg-yellow-600 hover:bg-yellow-700',
      iconBg: 'bg-yellow-100'
    }
  };

  const config = typeConfig[type];

  const handleConfirm = () => {
    if (inputValue.trim()) {
      onConfirm(inputValue.trim());
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleConfirm();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className={`${config.iconBg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
            {config.icon}
          </div>
          
          <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
            {title}
          </h3>
          
          {message && (
            <p className="text-gray-600 text-center mb-4">
              {message}
            </p>
          )}
          
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            autoFocus
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-6"
          />
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!inputValue.trim()}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition font-medium ${config.confirmBg} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

/**
 * Componente ConfirmDialog per conferme (cancellazione)
 */
function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmText = "Conferma", cancelText = "Annulla", type = "danger" }) {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      icon: <AlertTriangle className="w-12 h-12 text-red-500" />,
      confirmBg: 'bg-red-600 hover:bg-red-700',
      iconBg: 'bg-red-100'
    },
    warning: {
      icon: <AlertCircle className="w-12 h-12 text-yellow-500" />,
      confirmBg: 'bg-yellow-600 hover:bg-yellow-700',
      iconBg: 'bg-yellow-100'
    }
  };

  const config = typeConfig[type];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className={`${config.iconBg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
            {config.icon}
          </div>
          
          <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center whitespace-pre-wrap mb-6">
            {message}
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition font-medium ${config.confirmBg}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPALE FOLDERTREE
// ============================================================================

/**
 * Componente FolderTree per visualizzare cartelle e sottocartelle
 * con supporto per selezione, espansione e azioni
 */
const FolderTree = ({
  folders = [],
  documents = [],
  selectedFolder = '/',
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  showActions = true,
  multiSelect = false,
  selectedFolders = [],
  onToggleFolder
}) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set(['/']));
  const [hoveredFolder, setHoveredFolder] = useState(null);
  
  // Stati per i dialoghi
  const [createDialog, setCreateDialog] = useState({ isOpen: false, parentPath: '/' });
  const [renameDialog, setRenameDialog] = useState({ isOpen: false, path: '', currentName: '' });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, path: '' });

  // Costruisce l'albero gerarchico dalle cartelle piatte
  const buildFolderTree = () => {
    const tree = { path: '/', name: 'Tutti i documenti', children: {}, documents: [] };
    
    // Aggiungi tutte le cartelle all'albero
    folders.forEach(folderPath => {
      const parts = folderPath.split('/').filter(p => p);
      let current = tree;
      
      parts.forEach((part, index) => {
        const currentPath = '/' + parts.slice(0, index + 1).join('/');
        
        if (!current.children[part]) {
          current.children[part] = {
            path: currentPath,
            name: part,
            children: {},
            documents: []
          };
        }
        current = current.children[part];
      });
    });
    
    // Aggiungi documenti all'albero
    documents.forEach(doc => {
      const folderPath = doc.folder_path || '/';
      
      if (folderPath === '/') {
        tree.documents.push(doc);
      } else {
        const parts = folderPath.split('/').filter(p => p);
        let current = tree;
        
        parts.forEach(part => {
          if (current.children[part]) {
            current = current.children[part];
          }
        });
        
        current.documents.push(doc);
      }
    });
    
    return tree;
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleSelectFolder = (path) => {
    if (onSelectFolder) {
      onSelectFolder(path);
    }
  };

  // ========== GESTORI PER I DIALOGHI ==========

  const handleOpenCreateDialog = (parentPath) => {
    setCreateDialog({ isOpen: true, parentPath });
  };

  const handleConfirmCreate = (folderName) => {
    if (onCreateFolder) {
      const newPath = createDialog.parentPath === '/' 
        ? `/${folderName}` 
        : `${createDialog.parentPath}/${folderName}`;
      onCreateFolder(newPath);
    }
    setCreateDialog({ isOpen: false, parentPath: '/' });
  };

  const handleOpenRenameDialog = (path) => {
    const parts = path.split('/').filter(p => p);
    const currentName = parts[parts.length - 1];
    setRenameDialog({ isOpen: true, path, currentName });
  };

  const handleConfirmRename = (newName) => {
    if (onRenameFolder) {
      const parts = renameDialog.path.split('/').filter(p => p);
      const parentPath = '/' + parts.slice(0, -1).join('/');
      const newPath = parentPath === '/' ? `/${newName}` : `${parentPath}/${newName}`;
      onRenameFolder(renameDialog.path, newPath);
    }
    setRenameDialog({ isOpen: false, path: '', currentName: '' });
  };

  const handleOpenDeleteDialog = (path) => {
    setDeleteDialog({ isOpen: true, path });
  };

  const handleConfirmDelete = () => {
    if (onDeleteFolder) {
      onDeleteFolder(deleteDialog.path);
    }
    setDeleteDialog({ isOpen: false, path: '' });
  };

  // ========== RENDERING ==========

  const renderFolderNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.path);
    const isSelected = multiSelect ? selectedFolders.includes(node.path) : selectedFolder === node.path;
    const hasChildren = Object.keys(node.children).length > 0;
    const isHovered = hoveredFolder === node.path;

    const FolderIcon = isExpanded ? FolderOpen : Folder;
    const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

    const docCount = node.documents.length;
    const totalDocs = docCount + Object.values(node.children).reduce((sum, child) => {
      return sum + child.documents.length + Object.values(child.children).length;
    }, 0);

    return (
      <div key={node.path}>
        <div
          className={`flex items-center gap-2 py-2 px-2 rounded-lg cursor-pointer transition group ${
            !multiSelect && isSelected
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
          onClick={() => multiSelect ? onToggleFolder && onToggleFolder(node.path) : handleSelectFolder(node.path)}
          onMouseEnter={() => setHoveredFolder(node.path)}
          onMouseLeave={() => setHoveredFolder(null)}
        >
          {/* Checkbox (solo in modalità multi-selezione) */}
          {multiSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleFolder && onToggleFolder(node.path);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
          )}

          {/* Chevron per espandere/collassare */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(node.path);
              }}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <ChevronIcon className="w-4 h-4" />
            </button>
          )}

          {/* Icona cartella */}
          <FolderIcon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />

          {/* Nome cartella */}
          <span className={`flex-1 truncate ${multiSelect && isSelected ? 'text-blue-700 font-medium' : ''}`}>
            {node.name}
          </span>

          {/* Contatore documenti */}
          {totalDocs > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isSelected ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-600'
            }`}>
              {totalDocs}
            </span>
          )}

          {/* Azioni (mostrate on hover) */}
          {showActions && isHovered && node.path !== '/' && (
            <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => handleOpenCreateDialog(node.path)}
                className="p-1 hover:bg-blue-100 rounded transition"
                title="Crea sottocartella"
              >
                <FolderPlus className="w-3.5 h-3.5 text-blue-600" />
              </button>
              <button
                onClick={() => handleOpenRenameDialog(node.path)}
                className="p-1 hover:bg-yellow-100 rounded transition"
                title="Rinomina"
              >
                <Edit2 className="w-3.5 h-3.5 text-yellow-600" />
              </button>
              <button
                onClick={() => handleOpenDeleteDialog(node.path)}
                className="p-1 hover:bg-red-100 rounded transition"
                title="Elimina"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-600" />
              </button>
            </div>
          )}
        </div>

        {/* Sottocartelle */}
        {isExpanded && hasChildren && (
          <div>
            {Object.values(node.children)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(child => renderFolderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildFolderTree();

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col items-start gap-3 mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Folder className="w-5 h-5 text-gray-600" />
            Cartelle
          </h3>

          {showActions && (
            <button
              onClick={() => handleOpenCreateDialog('/')}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-1"
            >
              <FolderPlus className="w-4 h-4" />
              Nuova Cartella
            </button>
          )}
        </div>

        {/* Opzione "Tutti i documenti" */}
        <div
          className={`flex items-center gap-2 py-2 px-2 rounded-lg cursor-pointer transition mb-2 ${
            !multiSelect && selectedFolder === 'ALL'
              ? 'bg-blue-100 text-blue-700 font-medium'
              : multiSelect && selectedFolders.length === 0
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'hover:bg-gray-100'
          }`}
          onClick={() => multiSelect ? onToggleFolder && onToggleFolder('ALL') : handleSelectFolder('ALL')}
        >
          {/* Checkbox "seleziona tutti" in modalità multi-selezione */}
          {multiSelect && (
            <input
              type="checkbox"
              checked={selectedFolders.length === 0}
              onChange={(e) => {
                e.stopPropagation();
                onToggleFolder && onToggleFolder('ALL');
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
          )}
          <File className="w-5 h-5 text-gray-500" />
          <span className={`flex-1 ${multiSelect && selectedFolders.length === 0 ? 'font-medium text-blue-700' : ''}`}>
            Tutti i documenti
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            (!multiSelect && selectedFolder === 'ALL') || (multiSelect && selectedFolders.length === 0)
              ? 'bg-blue-200 text-blue-700'
              : 'bg-gray-200 text-gray-600'
          }`}>
            {documents.length}
          </span>
        </div>

        <div className="border-t border-gray-200 pt-2">
          {renderFolderNode(tree)}
        </div>
      </div>

      {/* Dialoghi */}
      <InputDialog
        isOpen={createDialog.isOpen}
        onClose={() => setCreateDialog({ isOpen: false, parentPath: '/' })}
        onConfirm={handleConfirmCreate}
        title="Crea Nuova Cartella"
        message={`Cartella padre: ${createDialog.parentPath}`}
        placeholder="Nome della cartella"
        confirmText="Crea"
        cancelText="Annulla"
        type="info"
      />

      <InputDialog
        isOpen={renameDialog.isOpen}
        onClose={() => setRenameDialog({ isOpen: false, path: '', currentName: '' })}
        onConfirm={handleConfirmRename}
        title="Rinomina Cartella"
        message={`Percorso: ${renameDialog.path}`}
        placeholder="Nuovo nome"
        defaultValue={renameDialog.currentName}
        confirmText="Rinomina"
        cancelText="Annulla"
        type="warning"
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, path: '' })}
        onConfirm={handleConfirmDelete}
        title="Elimina Cartella"
        message={`Sei sicuro di voler eliminare la cartella "${deleteDialog.path}" e tutti i suoi contenuti?\n\nQuesta operazione non può essere annullata.`}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />
    </>
  );
};

export default FolderTree;